import { DefaultImage } from "@bmi-digital/components";
import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { encodeBase64 } from "../../../utils/base64";
import { BaseProduct, ProductCategory } from "../types";
import getVergeScrews from "../calculation/vergeScrews";
import { getIsLeftDoubleWingedTile } from "../helpers/isLeftDoubleWingedTileSelected";
import { battenCalc, surface, type SurfaceProductsCount } from "./calculate";
import { CONTINGENCY } from "./constants";
import getRoofBasedAccessories from "./roofBasedAccessories";
import getMainTileTopClipQuantity from "./mainTileTopClips";
import getRidgeAndHipRolls from "./ridgeAndHipRolls";
import getRidgeAndHipFixings from "./ridgeAndHipFixings";
import type {
  Accessory,
  BasicResults,
  GutterHook,
  GutterVariant,
  LengthBasedProduct,
  ResultsRow,
  RidgeOption,
  Tile,
  Underlay,
  VentilationHood,
  VergeOption,
  WidthBasedProduct
} from "../types";
import type {
  Face,
  FaceWithBattens,
  Line,
  LinesMap,
  Measurements,
  RoofV2 as Roof
} from "../types/roof";

type Products =
  | Accessory
  | Tile
  | GutterVariant
  | GutterHook
  | Underlay
  | LengthBasedProduct
  | WidthBasedProduct
  | VentilationHood;

export type ProductRow = {
  name: string;
  code: string;
  externalProductCode: string;
  image?: string;
  category: ProductCategory;
  packSize?: number;
  baseQuantity: number; // Base quantity is quantity before being divided by packSize
  withContingency: boolean;
};

export const calculateBattensForFaces = (
  faces: Face[],
  mainTileVariant: Tile
): FaceWithBattens[] =>
  faces.map((face) => ({
    ...face,
    battens: battenCalc({
      vertices: face.vertices,
      pitch: face.pitch,
      mainTileVariant,
      subtract: Boolean(face.subtract)
    })
  }));

const adjustedSvg = renderToStaticMarkup(
  React.createElement(DefaultImage, { viewBox: "345 100 240 240" })
);

const placeholderImage = `data:image/svg+xml;base64,${encodeBase64(
  adjustedSvg
)}`;

export const convertProductRowToResultsRow = ({
  name,
  packSize = 1, // No packs by default
  baseQuantity,
  category,
  externalProductCode,
  image,
  withContingency
}: ProductRow): ResultsRow => ({
  category,
  image: image || placeholderImage,
  description: name,
  externalProductCode,
  packSize: packSize === 1 ? "-" : packSize.toString(),
  quantity: Math.ceil(
    Math.ceil(baseQuantity / packSize) * (withContingency ? 1 + CONTINGENCY : 1)
  )
});

const LONG_SCREW_PER_METER = 3.2;
const SCREW_PER_SEQUARE_METER = 10;
const STORM_BRACKET_PER_RIDGE_TILE = 2;

export type QuantitiesCalculatorProps = {
  measurements: Measurements;
  mainTileVariant: Tile;
  vergeOption?: VergeOption | WidthBasedProduct; // Not being provided means using mainTile to fill in verge tiles place
  ridge?: RidgeOption;
  ventilationHoods: VentilationHood[];
  underlay?: Underlay;
  gutteringVariant?: GutterVariant;
  gutteringHook?: GutterHook;
  downPipes?: number;
  downPipeConnectors?: number;
  roof: Roof;
};

// TODO: add some JS docs for the class methods + a separate test file.
class QuantitiesCalculator {
  results = new Map<string, ProductRow>(); // The key is the product "code"
  facesBattens: FaceWithBattens[];
  lines: LinesMap;

  constructor({
    measurements,
    mainTileVariant,
    vergeOption,
    ridge,
    ventilationHoods,
    underlay,
    gutteringVariant,
    gutteringHook,
    downPipes,
    downPipeConnectors,
    roof
  }: QuantitiesCalculatorProps) {
    const { faces, lines } = measurements;
    this.facesBattens = calculateBattensForFaces(faces, mainTileVariant);
    this.addSurfaceCoveringProducts(mainTileVariant, vergeOption);

    this.lines = lines;
    this.addLineProducts(mainTileVariant, ridge, mainTileVariant.hip);

    this.addRoofBasedAccessories(roof, mainTileVariant.hipStart, ridge);

    this.addVentilationHoods(ventilationHoods);

    this.addCalculatedAccessories(measurements, mainTileVariant, ridge);

    this.addOtherAccessories(mainTileVariant);

    this.addGuttering(
      lines.eave,
      gutteringVariant,
      gutteringHook,
      downPipes,
      downPipeConnectors
    );

    if (underlay) {
      this.addProduct(
        ProductCategory.Accessories,
        underlay,
        measurements.area
          ? Math.ceil(
              measurements.area /
                (underlay.length * (underlay.width - underlay.overlap))
            )
          : 0
      );
    }
  }

  addSurfaceCoveringProducts(
    mainTileVariant: Tile,
    verge?: VergeOption | WidthBasedProduct
  ) {
    const hasDoubleWingedTile = getIsLeftDoubleWingedTile(verge);
    const cloakedVerge = hasDoubleWingedTile ? undefined : verge;

    const hasHalfLeftVerges =
      mainTileVariant.brokenBond &&
      cloakedVerge?.halfLeft &&
      this.facesBattens.some(({ sides }) => sides[0] === "VERGE");

    const hasHalfRightVerges =
      mainTileVariant.brokenBond &&
      cloakedVerge?.halfRight &&
      this.facesBattens.some(({ sides }) => sides[1] === "VERGE");

    this.facesBattens.forEach((faceWithBattens) => {
      const faceTiles = surface({
        face: faceWithBattens,
        tile: mainTileVariant,
        hasHalfLeftVerges: Boolean(hasHalfLeftVerges),
        hasHalfRightVerges: Boolean(hasHalfRightVerges),
        cloakedVerge,
        hasDoubleWingedTile
      });

      this.addProduct(
        ProductCategory.Tiles,
        mainTileVariant,
        faceTiles.quantity,
        true
      );

      if (mainTileVariant.halfTile && faceTiles.half.quantity) {
        this.addProduct(
          ProductCategory.Tiles,
          mainTileVariant.halfTile,
          faceTiles.half.quantity
        );
      }

      if (hasDoubleWingedTile && faceTiles.leftDoubleWingedTiles) {
        this.addProduct(
          ProductCategory.Tiles,
          mainTileVariant.leftDoubleWingedTile!,
          faceTiles.leftDoubleWingedTiles
        );
      }

      if (cloakedVerge) {
        this.addVergeProducts(mainTileVariant, cloakedVerge, faceTiles);
      }
    });
  }

  addVergeProducts(
    mainTileVariant: Tile,
    vergeOption: VergeOption,
    surfaceProductsCount: SurfaceProductsCount
  ) {
    if (surfaceProductsCount.cloakedVerge.left) {
      this.addProduct(
        ProductCategory.Tiles,
        vergeOption.left,
        surfaceProductsCount.cloakedVerge.left
      );
    }

    if (surfaceProductsCount.cloakedVerge.right) {
      this.addProduct(
        ProductCategory.Tiles,
        vergeOption.right,
        surfaceProductsCount.cloakedVerge.right
      );
    }

    if (
      vergeOption.rightVergeTileType7 &&
      surfaceProductsCount.cloakedVerge.rightType7
    ) {
      this.addProduct(
        ProductCategory.Tiles,
        vergeOption.rightVergeTileType7,
        surfaceProductsCount.cloakedVerge.rightType7
      );
    }

    if (
      vergeOption.leftVergeTileType7 &&
      surfaceProductsCount.cloakedVerge.leftType7
    ) {
      this.addProduct(
        ProductCategory.Tiles,
        vergeOption.leftVergeTileType7,
        surfaceProductsCount.cloakedVerge.leftType7
      );
    }

    if (
      vergeOption.rightVergeTileType9 &&
      surfaceProductsCount.cloakedVerge.rightType9
    ) {
      this.addProduct(
        ProductCategory.Tiles,
        vergeOption.rightVergeTileType9,
        surfaceProductsCount.cloakedVerge.rightType9
      );
    }

    if (
      vergeOption.leftVergeTileType9 &&
      surfaceProductsCount.cloakedVerge.leftType9
    ) {
      this.addProduct(
        ProductCategory.Tiles,
        vergeOption.leftVergeTileType9,
        surfaceProductsCount.cloakedVerge.leftType9
      );
    }

    if (mainTileVariant.brokenBond) {
      if (surfaceProductsCount.cloakedVerge.halfLeft && vergeOption.halfLeft) {
        this.addProduct(
          ProductCategory.Tiles,
          vergeOption.halfLeft,
          surfaceProductsCount.cloakedVerge.halfLeft
        );
      }

      if (
        surfaceProductsCount.cloakedVerge.halfRight &&
        vergeOption.halfRight
      ) {
        this.addProduct(
          ProductCategory.Tiles,
          vergeOption.halfRight,
          surfaceProductsCount.cloakedVerge.halfRight
        );
      }
    }
  }

  addLineProducts(
    mainTileVariant: Tile,
    ridge?: RidgeOption,
    hip?: LengthBasedProduct
  ) {
    if (!this.lines) {
      throw new Error(`"lines" must be assigned before calling this function`);
    }

    this.addValleyMetalFlush(mainTileVariant);

    if (ridge) {
      this.addLineTilesForLine(this.lines.ridge, ridge);
    }

    if (hip) {
      this.addLineTilesForLine(this.lines.hip, hip);
    }
  }

  addValleyMetalFlush(mainTileVariant: Tile) {
    let valleyTopMetalFlushQuantity = 0;
    this.lines?.valley?.forEach(({ length, start, end, top, dormerStart }) => {
      if (start && mainTileVariant.valleyMetalFlushStart) {
        length -= this.addSingleAccessoryWithSubtraction(
          mainTileVariant.valleyMetalFlushStart
        );
      }

      if (end && mainTileVariant.valleyMetalFlushEnd) {
        length -= this.addSingleAccessoryWithSubtraction(
          mainTileVariant.valleyMetalFlushEnd
        );
      }

      if (top && mainTileVariant.valleyMetalFlushTop) {
        // Count double the needed quantity of top metal flush then divide later
        length -= mainTileVariant.valleyMetalFlushTop.coverLength;
        valleyTopMetalFlushQuantity++;
      }

      if (dormerStart && mainTileVariant.valleyMetalFlushDormerStart) {
        length -= this.addSingleAccessoryWithSubtraction(
          mainTileVariant.valleyMetalFlushDormerStart
        );
      }

      if (mainTileVariant.valleyMetalFlush) {
        this.addProduct(
          ProductCategory.Accessories,
          mainTileVariant.valleyMetalFlush,
          Math.ceil(length / mainTileVariant.valleyMetalFlush.coverLength)
        );
      }
    });

    if (mainTileVariant.valleyMetalFlushTop && valleyTopMetalFlushQuantity) {
      if (valleyTopMetalFlushQuantity % 2) {
        throw new Error(
          "There is an odd number of lines needing top valley metal flush which is used as only one per pair"
        );
      }

      this.addProduct(
        ProductCategory.Accessories,
        mainTileVariant.valleyMetalFlushTop,
        valleyTopMetalFlushQuantity / 2
      );
    }
  }

  // Returns how much to subtrat from length
  addSingleAccessoryWithSubtraction(accessory: LengthBasedProduct) {
    this.addProduct(ProductCategory.Accessories, accessory, 1);
    return accessory.coverLength;
  }

  addLineTilesForLine(line: Line[], tileProduct: LengthBasedProduct) {
    line.forEach(({ length }) =>
      this.addLengthBasedProduct(ProductCategory.Tiles, tileProduct, length)
    );
  }

  addLengthBasedProduct(
    category: ProductCategory,
    product: LengthBasedProduct,
    length: number
  ) {
    this.addProduct(category, product, Math.ceil(length / product.coverLength));
  }

  addVentilationHoods(ventilationHoods: VentilationHood[]) {
    ventilationHoods.forEach((item) =>
      this.addProduct(ProductCategory.Ventilation, item, 1)
    );
  }

  addGuttering(
    eave: Line[],
    gutteringVariant?: GutterVariant,
    gutteringHook?: GutterHook,
    downPipes = 0,
    downPipeConnectors = 0
  ) {
    eave.forEach(({ length }) => {
      if (gutteringVariant) {
        this.addProduct(
          ProductCategory.Accessories,
          gutteringVariant,
          Math.ceil(length / gutteringVariant.length)
        );
      }

      if (gutteringHook) {
        this.addProduct(
          ProductCategory.Accessories,
          gutteringHook,
          Math.ceil(length / gutteringHook.length)
        );
      }
    });

    if (gutteringVariant?.downPipe) {
      this.addProduct(
        ProductCategory.Accessories,
        gutteringVariant.downPipe,
        downPipes
      );
    }

    if (gutteringVariant?.downPipeConnector) {
      this.addProduct(
        ProductCategory.Accessories,
        gutteringVariant.downPipeConnector,
        downPipeConnectors
      );
    }
  }

  addCalculatedAccessories(
    { lines, area }: Measurements,
    mainTileVariant: Tile,
    ridge?: RidgeOption
  ) {
    this.addMainTileSideClips(mainTileVariant);
    this.addMainTileTopClips(mainTileVariant);
    this.addRidgeAndHipFixings(mainTileVariant, ridge);
    this.addVergeScrews(mainTileVariant);
    this.addRidgeAndHipRolls(mainTileVariant);

    const ridgeTiles = ridge ? this.getProductQuantity(ridge.code) : 0;

    let longScrews = 0;

    if (mainTileVariant.longScrew) {
      const hipMeters = lines.hip.reduce((acc, v) => acc + v.length, 0) / 100;
      const ridgeMeters =
        lines.ridge.reduce((acc, v) => acc + v.length, 0) / 100;
      const eaveMeters = lines.eave.reduce((acc, v) => acc + v.length, 0) / 100;
      const leftVergeMeters =
        lines.leftVerge.reduce((acc, v) => acc + v.length, 0) / 100;
      const rightVergeMeters =
        lines.rightVerge.reduce((acc, v) => acc + v.length, 0) / 100;

      longScrews = Math.ceil(
        (hipMeters +
          ridgeMeters +
          eaveMeters +
          leftVergeMeters +
          rightVergeMeters) *
          LONG_SCREW_PER_METER
      );

      this.addProduct(
        ProductCategory.Fixings,
        mainTileVariant.longScrew,
        longScrews
      );
    }

    if (mainTileVariant.screw) {
      this.addProduct(
        ProductCategory.Fixings,
        mainTileVariant.screw,
        area
          ? Math.ceil(
              (area / 10000) /* area in meters */ * SCREW_PER_SEQUARE_METER -
                longScrews
            )
          : 0
      );
    }

    if (ridgeTiles && mainTileVariant.stormBracket) {
      this.addProduct(
        ProductCategory.Fixings,
        mainTileVariant.stormBracket,
        ridgeTiles * STORM_BRACKET_PER_RIDGE_TILE
      );
    }

    if (mainTileVariant.finishingKit) {
      this.addProduct(ProductCategory.Fixings, mainTileVariant.finishingKit, 1);
    }

    if (!mainTileVariant.eaveAccessories?.length) {
      return;
    }

    lines.eave.forEach((eaveLine) =>
      mainTileVariant.eaveAccessories!.forEach((accessory) => {
        this.addProduct(
          accessory.category || ProductCategory.Accessories,
          accessory,
          Math.ceil(eaveLine.length / accessory.coverLength)
        );
      })
    );
  }

  addOtherAccessories(tileVariant: Tile) {
    tileVariant.accessories?.forEach((accessory) => {
      if (accessory.category === ProductCategory.Sealing) {
        this.addProduct(ProductCategory.Sealing, accessory, 0);
        return;
      }

      this.addProduct(ProductCategory.Accessories, accessory, 0);
    });
  }

  addRoofBasedAccessories(
    roof: Roof,
    hipStart?: BaseProduct,
    ridge?: RidgeOption
  ) {
    const { ridgeStartCount, ridgeEndCount, yRidgeCount, hipStartCount } =
      getRoofBasedAccessories(roof);

    if (hipStart && hipStartCount) {
      this.addProduct(ProductCategory.Tiles, hipStart, hipStartCount);
    }

    if (!ridge) {
      return;
    }

    if (ridge.tRidge) {
      this.addProduct(ProductCategory.Tiles, ridge.tRidge, 0);
    }
    if (ridge.yRidge && yRidgeCount) {
      this.addProduct(ProductCategory.Tiles, ridge.yRidge, yRidgeCount);
    }
    if (ridge.ridgeEnd && ridgeEndCount) {
      this.addProduct(ProductCategory.Tiles, ridge.ridgeEnd, ridgeEndCount);
    }
    if (ridge.ridgeStart && ridgeStartCount) {
      this.addProduct(ProductCategory.Tiles, ridge.ridgeStart, ridgeStartCount);
    }
  }

  addMainTileSideClips(tileVariant: Tile) {
    if (!tileVariant.mainTileSideClip) {
      return;
    }

    const totalMainTiles = this.getProductQuantity(tileVariant.code);
    const calculatedSideClips = Math.ceil(totalMainTiles * 0.55);

    this.addProduct(
      ProductCategory.Fixings,
      tileVariant.mainTileSideClip,
      calculatedSideClips
    );
  }

  addMainTileTopClips(tileVariant: Tile) {
    const mainTileTopClipsQuantity = getMainTileTopClipQuantity(
      tileVariant,
      this.facesBattens,
      this.lines.ridge
    );

    if (mainTileTopClipsQuantity) {
      this.addProduct(
        ProductCategory.Fixings,
        tileVariant.mainTileTopClip!,
        mainTileTopClipsQuantity
      );
    }
  }

  addRidgeAndHipRolls(tileVariant: Tile) {
    const ridgeAndHipRollsQuantity = getRidgeAndHipRolls(
      this.lines,
      tileVariant.ridgeAndHipRoll
    );

    if (ridgeAndHipRollsQuantity) {
      this.addProduct(
        ProductCategory.Accessories,
        tileVariant.ridgeAndHipRoll!,
        ridgeAndHipRollsQuantity
      );
    }
  }

  addVergeScrews(tileVariant: Tile) {
    const vergeScrewsQuantity = getVergeScrews(
      tileVariant,
      this.getProductQuantity.bind(this)
    );

    if (vergeScrewsQuantity) {
      this.addProduct(
        ProductCategory.Fixings,
        tileVariant.vergeScrew!,
        vergeScrewsQuantity
      );
    }
  }

  addRidgeAndHipFixings(tileVariant: Tile, ridge?: RidgeOption) {
    const ridgeAndHipFixingsQuantity = getRidgeAndHipFixings(
      this.getProductQuantity.bind(this),
      ridge,
      tileVariant.hip
    );

    if (ridgeAndHipFixingsQuantity && tileVariant.clip) {
      this.addProduct(
        ProductCategory.Fixings,
        tileVariant.clip,
        ridgeAndHipFixingsQuantity
      );
    }

    if (ridgeAndHipFixingsQuantity && tileVariant.ridgeAndHipScrew) {
      this.addProduct(
        ProductCategory.Fixings,
        tileVariant.ridgeAndHipScrew,
        ridgeAndHipFixingsQuantity
      );
    }
  }

  getProductQuantity(code: string) {
    return (this.results.get(code) || {}).baseQuantity || 0;
  }

  addProduct(
    category: ProductCategory,
    product: Products,
    baseQuantity: number,
    withContingency?: boolean
  ) {
    const productFromMap = this.results.get(product.code);

    if (productFromMap && productFromMap.category !== category) {
      throw new Error(
        `Product of code: ${product.code} is being added to two different category`
      );
    }

    const { baseQuantity: oldBaseQuantity = 0, ...otherCurrentProductProps } =
      productFromMap || {};

    const newMapProduct: ProductRow = {
      ...otherCurrentProductProps,
      image: product.mainImage,
      code: product.code,
      externalProductCode: product.externalProductCode,
      name: product.name,
      packSize: product.packSize,
      category,
      baseQuantity: oldBaseQuantity + baseQuantity,
      withContingency
    };

    this.results.set(product.code, newMapProduct);
  }

  // Those have quantity as the number of packs
  getResultsRowsByCategory(): BasicResults {
    const result: BasicResults = {
      tiles: [],
      fixings: [],
      sealing: [],
      ventilation: [],
      accessories: []
    };

    this.results.forEach((product) => {
      result[product.category].push(convertProductRowToResultsRow(product));
    });

    return result;
  }
}

export default QuantitiesCalculator;
